/**
 * Comprehensive formatting utilities for the Edges design system
 *
 * This module provides a unified formatting system with support for:
 * - Text formatting (case conversion, truncation, etc.)
 * - Number formatting (decimals, thousands separators, compact notation)
 * - Currency formatting (multiple currencies and display options)
 * - Date formatting (relative time, custom formats, timezones)
 * - Boolean formatting (custom true/false text)
 * - Energy units (Wh, kWh, MWh, GWh with auto-scaling)
 * - Temperature units (Celsius, Fahrenheit, Kelvin)
 * - Distance units (meters, kilometers, miles, feet)
 * - Phone number formatting (US and international)
 * - Custom formatters via the FormatRegistry
 */

export * from "./boolean";
export * from "./capacity";
export * from "./component-formatter";
export * from "./current";
export * from "./date";
export * from "./distance";
export * from "./energy";
export * from "./number";
export * from "./phone";
export * from "./power";
// Export the registry
export { FormatRegistry } from "./registry";
export * from "./resistance";
export * from "./temperature";
// Export all formatting utilities
export * from "./text";
// Export all types
export * from "./types";
export * from "./voltage";

import { formatBoolean } from "./boolean";
import { formatCurrent } from "./current";
import { formatDate } from "./date";
import { formatDistance } from "./distance";
import { formatEnergy } from "./energy";
import { formatCurrency, formatNumber, formatPercent } from "./number";
import { formatPhone } from "./phone";
import { formatPower } from "./power";
import { formatResistance } from "./resistance";
import { formatTemperature } from "./temperature";
import { formatText } from "./text";
// Import types for the main formatting function
import type { CustomFormat, FieldFormat, FieldValue, FormattedValue, PercentageFormat } from "./types";
import { formatVoltage } from "./voltage";

/**
 * Format a value as a percentage
 * Internal helper for the percentage format type
 */
const formatPercentage = (value: FieldValue, format: PercentageFormat): FormattedValue => {
  if (value == null) return format.nullText ?? null;
  if (format.hideWhenNull && value === null) return null;

  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return format.nullText ?? null;

  return formatPercent(num, format.decimals ?? 0);
};

/**
 * Main formatting function that routes to the appropriate formatter
 * based on the format type
 *
 * @param value - The value to format
 * @param format - The format specification
 * @returns The formatted value or null
 *
 * @example
 * ```typescript
 * // Format as currency
 * formatFieldValue(1234.56, { type: "currency", currency: "USD" })
 * // Returns: "$1,234.56"
 *
 * // Format as relative time
 * formatFieldValue(new Date(), { type: "date", relative: true })
 * // Returns: "just now"
 *
 * // Format with custom formatter
 * formatFieldValue("hello", {
 *   type: "custom",
 *   formatter: (val) => String(val).toUpperCase()
 * })
 * // Returns: "HELLO"
 * ```
 */
export const formatFieldValue = (value: FieldValue, format: FieldFormat): FormattedValue => {
  switch (format.type) {
    case "text":
      return formatText(value, format);
    case "number":
      return formatNumber(value, format);
    case "percentage":
      return formatPercentage(value, format);
    case "currency":
      return formatCurrency(value, format);
    case "date":
      return formatDate(value, format);
    case "boolean":
      return formatBoolean(value, format);
    case "energy":
      return formatEnergy(value, format);
    case "power":
      return formatPower(value, format);
    case "voltage":
      return formatVoltage(value, format);
    case "current":
      return formatCurrent(value, format);
    case "resistance":
      return formatResistance(value, format);
    case "temperature":
      return formatTemperature(value, format);
    case "distance":
      return formatDistance(value, format);
    case "phone":
      return formatPhone(value, format);
    case "custom": {
      const customFormat = format as CustomFormat;
      if (customFormat.hideWhenNull && (value === null || value === undefined)) {
        return null;
      }
      if (value == null) return customFormat.nullText ?? null;
      return customFormat.formatter(value, customFormat.options);
    }
    default:
      // Fallback to string conversion
      return value != null ? String(value) : null;
  }
};

/**
 * Convenience function to create a format object
 *
 * @example
 * ```typescript
 * const currencyFormat = createFormat("currency", { currency: "EUR" });
 * const dateFormat = createFormat("date", { relative: true });
 * ```
 */
export const createFormat = <T extends FieldFormat["type"]>(
  type: T,
  options?: Omit<Extract<FieldFormat, { type: T }>, "type">
): Extract<FieldFormat, { type: T }> => {
  return { type, ...options } as Extract<FieldFormat, { type: T }>;
};
